import React from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { CONTROL_RADIUS } from './detailControls'

// RN76 (CA12.1/12.3/4.7/A4): se muestra antes de cualquier acción que abandone la ventana con
// cambios sin guardar en General — cerrar el detalle, saltar a otra orden, abrir Workflow o
// Balanced. Tres opciones, sin cuarta vía silenciosa: Guardar, Descartar y Seguir editando.
//
// ⚠️⚠️ EL LIENZO PIDE DOS BOTONES EN CADA CASO —«seguir editando / cerrar y descartar» al cerrar y
// «seguir editando / guardar y abrir» al abrir el workflow— Y AQUÍ SIGUEN LOS TRES. **Decisión del
// PO, consultada expresamente: el lienzo NO deroga S96.** El motivo: quien cierra con cambios sin
// guardar casi nunca quiere perderlos, y con dos botones la única salida que conserva el trabajo
// es volver atrás y guardar a mano — se le quita la opción buena para ahorrarle un botón.
// Lo que sí se ha adoptado del lienzo es todo lo demás: qué se dice en cada momento, qué campos
// están en juego, y qué botón pesa más.
//
// 📌 LO QUE CAMBIA SEGÚN EL MOTIVO ES CUÁL ES LA SALIDA BUENA, y por eso el motivo viaja con la
// acción en vez de adivinarse aquí:
//   · al CERRAR, la salida buena es no perder nada: «descartar» es lo destructivo, va a la derecha
//     y en rojo, y guardar es un botón normal.
//   · al TOMAR LA VENTANA (workflow/balanced), nadie abre el workflow para perder lo que acaba de
//     escribir: la salida buena es GUARDAR Y SEGUIR, así que es el botón lleno.
// En los dos casos «seguir editando» se lee primero y NINGUNO es el predeterminado: un `autoFocus`
// convertiría en un enter distraído la decisión que este aviso existe para forzar.
const UnsavedChangesPrompt = ({
  open,
  reason = 'close',
  fields = [],
  onSave = () => {},
  onDiscard = () => {},
  onKeepEditing = () => {},
  saving = false
}) => {
  const { t_ } = useSmartTranslation()
  const takeover = reason === 'takeover'

  // ⚠ Los campos en juego se NOMBRAN, no se cuentan: «2 cambios» obliga a repasar la pantalla
  // entera para saber qué se está a punto de perder. Si no llegan nombres —el aviso puede
  // dispararse desde sitios que no los conocen— se dice cuántos, que es lo que se sabe, en vez de
  // una frase a medias.
  const named = fields.filter((field) => !!field)
  const count = named.length

  return (
    <Dialog open={open} onClose={saving ? () => {} : onKeepEditing} data-testid='unsaved-changes-prompt'>
      <DialogContent>
        <Stack direction='row' spacing={1.5} alignItems='flex-start'>
          <WarningAmberRounded fontSize='small' sx={{ color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
              {takeover ? t_('The workflow takes over this window') : t_('You have unsaved changes')}
            </Typography>
            <Typography data-testid='unsaved-changes-detail' variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              {takeover
                ? t_('It does not stack on top: the detail closes to open it, and you have unsaved changes in {{fields}}', { fields: named.join(', ') })
                : (count > 0
                    ? t_('If you close now you lose {{fields}}', { fields: named.join(', ') })
                    : t_('You have unsaved changes'))}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid='unsaved-changes-keep-editing' onClick={onKeepEditing} disabled={saving} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>
          {t_('Keep editing')}
        </Button>
        <Button
          data-testid='unsaved-changes-save'
          onClick={onSave}
          disabled={saving}
          variant={takeover ? 'contained' : 'text'}
          sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS, fontWeight: 700 }}
        >
          {takeover ? t_('Save and open') : t_('Save')}
        </Button>
        <Button
          data-testid='unsaved-changes-discard'
          onClick={onDiscard}
          disabled={saving}
          sx={(theme) => ({
            textTransform: 'none',
            borderRadius: CONTROL_RADIUS,
            fontWeight: 700,
            color: theme.palette.error.main,
            border: `1px solid ${alpha(theme.palette.error.main, 0.38)}`,
            backgroundColor: alpha(theme.palette.error.main, 0.14),
            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.22) }
          })}
        >
          {takeover ? t_('Discard') : t_('Close and discard')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UnsavedChangesPrompt
